'use client';

import { ChevronDown, ChevronUp, Navigation, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { formatDistanceM, formatPrice, nearestChargers } from '@/lib/geo';
import type { ChargerFC } from '@/lib/api';
import type { Charger } from '@/types/charger';

export default function NearbyCard({
  data,
  origin,
  settlement,
  onSelect,
  onNavigate,
}: {
  data: ChargerFC;
  origin: { lng: number; lat: number } | null;
  settlement: string;
  onSelect: (c: Charger) => void;
  onNavigate: (c: Charger) => void;
}) {
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(true);
  const items = useMemo(() => {
    if (!origin) return [];
    return nearestChargers(data, origin.lat, origin.lng, 5);
  }, [data, origin]);

  useEffect(() => {
    if (window.matchMedia('(min-width: 768px)').matches) {
      setOpen(true);
      setHidden(false);
    }
  }, []);

  if (!origin || items.length === 0) return null;
  const title = settlement ? `Közelben · ${settlement}` : 'Közelben';

  if (hidden) {
    return (
      <button
        type="button"
        className="pointer-events-auto rounded-full border border-white/50 bg-white/70 px-3 py-1.5 text-xs font-semibold text-slate-800 shadow backdrop-blur-md md:hidden"
        onClick={() => setHidden(false)}
      >
        Közelben
      </button>
    );
  }

  return (
    <div className="pointer-events-auto w-[min(100%,20rem)] overflow-hidden rounded-2xl border border-white/50 bg-white/55 shadow-lg backdrop-blur-md">
      <div className="flex items-center gap-1 pr-1">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center justify-between gap-2 px-3 py-2 text-left"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="min-w-0 truncate text-xs font-semibold text-slate-800">{title}</span>
          {open ? <ChevronUp className="h-4 w-4 shrink-0 text-slate-500" /> : <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" />}
        </button>
        <button
          type="button"
          className="rounded-full p-1.5 text-slate-400 hover:bg-white/60 md:hidden"
          title="Elrejtés"
          onClick={() => setHidden(true)}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {open ? (
        <ul className="divide-y divide-white/40 border-t border-white/40">
          {items.map((c) => {
            const price = formatPrice(c);
            return (
              <li key={c.id}>
                <div className="flex items-stretch gap-1">
                  <button
                    type="button"
                    className="min-w-0 flex-1 px-3 py-2 text-left hover:bg-white/40"
                    onClick={() => onSelect(c)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-xs font-medium text-slate-900">{c.name || c.settlement}</p>
                      <span className="shrink-0 text-[11px] font-semibold text-emerald-700">{formatDistanceM(c.distanceM)}</span>
                    </div>
                    <p className="truncate text-[11px] text-slate-500">
                      {c.operator}
                      {c.maxKw > 0 ? ` · ${Math.round(c.maxKw)} kW` : ''}
                    </p>
                    {price ? (
                      <p className="mt-0.5 truncate text-[11px] font-medium text-slate-700">{price}</p>
                    ) : null}
                  </button>
                  <button
                    type="button"
                    className="shrink-0 px-2 text-emerald-700 hover:bg-white/50"
                    title="Útvonal a térképen"
                    onClick={() => onNavigate(c)}
                  >
                    <Navigation className="h-4 w-4" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
